import { formatDateToShortDateString } from 'util/formatDate';
import { selectHasMatch } from 'util/selector/routerSelector';
import { selectModeType } from 'util/selector/querySelector';

import { AppState } from 'reduxStore/appStore';
import { createSelector } from '@reduxjs/toolkit';
import {
  selectIsPulseProjectKind,
  selectIsPulseTemplateProjectKind,
} from 'reduxStore/project/selectors';
import { selectResponseRateByProjectId } from 'reduxStore/responseRate/selectors';
import { ResponseRate } from 'model/ResponseRate';
import { AppRoute } from 'app/route/app';
import { ModeType } from 'register/ModeType';

import { STORE_NAME } from './initialState';

const selectDomain = (state: AppState) => state[STORE_NAME];

/* Summary pulse compared */

export const selectSummaryPulseComparedResource = createSelector(
  selectDomain,
  (domain) => domain.pulseCompared
);

export const selectSummaryPulseCompared = createSelector(
  selectSummaryPulseComparedResource,
  ({ data }) => data
);

export const selectIsSummaryPulseComparedOrCategoryPage = createSelector(
  selectIsPulseProjectKind,
  selectHasMatch(AppRoute.Home.SUMMARY),
  selectHasMatch(AppRoute.Home.Summary.REPORT),
  selectModeType,
  (isPulseProjectKind, isSummaryPage, isCategoryPage, modeType) =>
    isPulseProjectKind && modeType === ModeType.COMPARED && (isSummaryPage || isCategoryPage)
);

export const selectIsCombineModeLockFromSummaryPulseCompared = createSelector(
  selectSummaryPulseCompared,
  (data) => !!data?.combineModeLock
);

/* Rest Summary selectors */

export const selectSummaryCombineResource = createSelector(
  selectDomain,
  (domain) => domain.combine
);

export const selectSummaryCombine = createSelector(
  selectSummaryCombineResource,
  ({ data }) => data
);

export const selectSummaryCompareResource = createSelector(
  selectDomain,
  (domain) => domain.compare
);

export const selectSummaryCompare = createSelector(
  selectSummaryCompareResource,
  ({ data }) => data
);

export const selectSummaryCompareGroups = createSelector(
  selectSummaryCompare,
  (compare) => compare[0]?.groups
);

export const selectSummaryCompareGroupsLength = createSelector(
  selectSummaryCompareGroups,
  (groups) => groups?.length
);

export const selectTrends = createSelector(selectSummaryCombine, (combine) => combine?.trends);

export const selectBenchmark = createSelector(
  selectSummaryCombine,
  (combine) => combine?.benchmark
);

export const selectScore = createSelector(selectSummaryCombine, (combine) => combine?.score);

export const selectPulseSummaryResource = createSelector(selectDomain, (domain) => domain.pulses);

export const selectPulseSummary = createSelector(selectPulseSummaryResource, ({ data }) => data);

export const selectPulsesList = createSelector(selectPulseSummary, (pulses) => pulses?.list);

export const selectAggregatedBenchmark = createSelector(
  selectPulseSummary,
  (summary) => summary?.aggregatedBenchmark || null
);

export const selectProjectLabelByProjectId = (projectId: number) =>
  createSelector(
    selectIsPulseTemplateProjectKind,
    selectResponseRateByProjectId(projectId),
    (isPulseTemplate, responseRate) => {
      return isPulseTemplate
        ? 'your last WorkBuzz pulse'
        : formatDateToShortDateString((responseRate as ResponseRate)?.date);
    }
  );

export const selectIsSummaryPulseCombinedOrCategoryPage = createSelector(
  selectIsPulseProjectKind,
  selectHasMatch(AppRoute.Home.SUMMARY),
  selectHasMatch(AppRoute.Home.Summary.REPORT),
  selectModeType,
  (isPulseProjectKind, isSummaryPage, isCategoryPage, modeType) =>
    isPulseProjectKind && modeType === ModeType.COMBINED && (isSummaryPage || isCategoryPage)
);
